/**
 * ShellMenu.tsx — the shell's ONE popover-menu keyboard model (Sidebar v2
 * slice S1, RULED 2026-10-10 Q1), lifted out of the account menu slice 2b
 * wrote so the department switcher and the account menu share it:
 *   · Enter / Space open (the trigger's click); ArrowDown / ArrowUp on the
 *     trigger open it and focus the first item;
 *   · ArrowDown / ArrowUp move and WRAP; Home / End jump to the ends;
 *   · Escape closes and RETURNS FOCUS to the trigger; Tab closes and lets
 *     focus move on; an outside mousedown closes; an inside one does not;
 *   · picking an item closes, returns focus, then runs the handler.
 * Items are `menuitem` OR `menuitemradio` (the switcher's departments, the
 * Theme group) — one selector covers both.
 *
 * A shell menu is a POPOVER, never a dialog: it must not carry aria-modal.
 * The single-key guard (src/lib/keyboardGuard.ts) treats an open menu as
 * inert through TYPING_SELECTOR's `[role="menu"]` — focus sits on an item
 * inside the menu while it is open — not through the modal selector, which is
 * the dialogs' and would also stop every other document shortcut.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

/** Every item kind a shell menu renders. */
export const MENU_ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"]'

/** `closeSignal` (Sidebar v2 S2, RULED 2026-10-11 (5)): the shell bumps it on
 *  every rail toggle and an open menu closes. A menu records the signal it was
 *  opened under, so "open" is DERIVED — a bump closes it in the same render,
 *  with no effect and no second render. Returning focus to the trigger is the
 *  shell's (it knows, in the toggle's own handler, whether focus was inside). */
export function useShellMenu<TTrigger extends HTMLElement = HTMLButtonElement, TMenu extends HTMLElement = HTMLDivElement>(closeSignal = 0) {
  const [openAt, setOpenAt] = useState<number | null>(null)
  const open = openAt === closeSignal
  const setOpen = useCallback((next: boolean) => setOpenAt(next ? closeSignal : null), [closeSignal])
  const triggerRef = useRef<TTrigger>(null)
  const menuRef = useRef<TMenu>(null)
  const close = useCallback((refocus: boolean) => {
    setOpenAt(null)
    if (refocus) triggerRef.current?.focus()
  }, [])
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); close(true) } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open, close, setOpen])
  useEffect(() => {
    if (!open || !menuRef.current) return
    const first = menuRef.current.querySelector<HTMLElement>(MENU_ITEM_SELECTOR)
    if (first && !menuRef.current.contains(document.activeElement)) first.focus()
  }, [open])
  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR) ?? [])
  const onTriggerKey = (e: React.KeyboardEvent<TTrigger>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setOpen(true) }
  }
  const onMenuKey = (e: React.KeyboardEvent<TMenu>) => {
    const list = items()
    if (list.length === 0) return
    const i = list.indexOf(document.activeElement as HTMLElement)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      list[e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length].focus()
    } else if (e.key === 'Home') { e.preventDefault(); list[0].focus() }
    else if (e.key === 'End') { e.preventDefault(); list[list.length - 1].focus() }
    else if (e.key === 'Tab') { setOpen(false) }
  }
  /** Close, return focus to the trigger, then run the item's handler. */
  const pick = (fn: () => void) => () => { close(true); fn() }
  const toggle = () => setOpen(!open)
  return { open, toggle, close, triggerRef, menuRef, onTriggerKey, onMenuKey, pick }
}
